import { ColumnTypes, ITableColumn, ITableRow, RowValue } from '@makyoapp/makyo-sdk/models/Table'

import { ICell } from '..'
import { AddressCell, IAddressColumnSettings } from '../address'
import { ArrayCell } from '../array'
import { AttachmentCell } from '../attachment'
import { BarcodeCell, IBarcodeSettings } from '../barcode'
import { BooleanCell } from '../boolean'
import { ColorCell, IColorColumnSettings } from '../color'
import { ConnectionCell, IConnectionColumnSettings } from '../connection'
import { CoordinatesCell } from '../coordinates'
import { DateTimeCell, IDateTimeColumnSettings } from '../dateTime'
import { EmailCell } from '../email'
import { FunctionCell, IFunctionSettings } from '../function'
import { IconCell } from '../icon'
import { ImageCell, TImageSettings } from '../image'
import { ImageArrayCell } from '../imageArray'
import { ILookupColumnSettings, LookupCell } from '../lookup'
import { IMathColumnSettings, MathCell } from '../math'
import { INumberColumnSettings, NumberCell } from '../number'
import { PhoneCell } from '../phone'
import { IQRCodeColumnSettings, QRCodeCell } from '../qrcode'
import { IRatingColumnSettings, RatingCell } from '../rating'
import { ISingleCellColumnSettings, SingleCellCell } from '../single-cell'
import { SlugCell } from '../slug'
import { ITemplateColumnSettings, TemplateCell } from '../template'
import { TextCell } from '../text'

/**
 * Parse cell data using its (standard) column settings.
 * This method will use the column model intance suitable for cell
 */
export function parseStandardColumn(
  column: ITableColumn,
  value: RowValue,
  columnsMap: Map<string, ITableColumn>,
  rowData: ITableRow,
  timezone?: string
): ICell {
  return parseStdColumn(column._Type, column._Settings, value, columnsMap, rowData, column._Name, {
    resolvedColumnType: column._ResolvedType,
    resolvedColumnSettings: column._ResolvedColumnSettings,
    timezone,
  })
}

/**
 * Internal function to parse cell data for standard column
 */
export function parseStdColumn(
  columnType: ColumnTypes,
  settings: Record<string, any>,
  value: RowValue,
  columnsMap?: Map<string, ITableColumn>,
  rowData?: ITableRow,
  columnName?: string,
  meta?: {
    resolvedColumnType?: ColumnTypes
    resolvedColumnSettings?: Record<string, any>
    timezone?: string
  }
): ICell {
  if (value === '_CELL_LOADING_') {
    return new TextCell(value, null)
  }
  switch (columnType) {
    case ColumnTypes.Number: {
      return new NumberCell(value, settings as INumberColumnSettings)
    }
    case ColumnTypes.Boolean: {
      return new BooleanCell(value, null)
    }
    case ColumnTypes.Address: {
      return new AddressCell(value, settings as IAddressColumnSettings)
    }
    case ColumnTypes.Array: {
      return new ArrayCell(value, null)
    }
    case ColumnTypes.ImageArray: {
      return new ImageArrayCell(value, null)
    }
    case ColumnTypes.Color: {
      return new ColorCell(value, settings as unknown as IColorColumnSettings)
    }
    case ColumnTypes.DateTime: {
      return new DateTimeCell(value, settings as unknown as IDateTimeColumnSettings, meta?.timezone)
    }
    case ColumnTypes.Attachment: {
      return new AttachmentCell(value, null)
    }
    case ColumnTypes.Icon: {
      return new IconCell(value, null)
    }
    case ColumnTypes.Email: {
      return new EmailCell(value, null)
    }
    case ColumnTypes.Phone: {
      return new PhoneCell(value, null)
    }
    case ColumnTypes.Rating: {
      return new RatingCell(value, settings as unknown as IRatingColumnSettings)
    }
    case ColumnTypes.Coordinate: {
      return new CoordinatesCell(value, null)
    }

    // update: some parsers will be moved back here
    case ColumnTypes.Image: {
      if (!columnsMap || !rowData) {
        return new TextCell(value, null)
      }
      return parseImageColumn(settings, value, columnsMap, rowData)
    }
    case ColumnTypes.QRCode: {
      if (!columnsMap || !rowData) {
        return new TextCell(value, null)
      }
      return parseQrCodeColumn(settings, value, columnsMap, rowData)
    }
    case ColumnTypes.Barcode: {
      if (!columnsMap || !rowData) {
        return new TextCell(value, null)
      }
      return parseBarCodeColumn(settings, value, columnsMap, rowData)
    }
    case ColumnTypes.Slug: {
      if (!columnsMap || !rowData) {
        return new TextCell(value, null)
      }
      return parseSlugColumn(settings, value, columnsMap, rowData, columnName)
    }

    // advanced columns

    case ColumnTypes.Connection: {
      return new ConnectionCell(value as any[], settings as unknown as IConnectionColumnSettings)
    }
    case ColumnTypes.Lookup: {
      return new LookupCell(
        value as any[],
        settings as unknown as ILookupColumnSettings,
        meta?.resolvedColumnType,
        meta?.resolvedColumnSettings,
        meta?.timezone
      )
    }
    case ColumnTypes.SingleCell: {
      return new SingleCellCell(
        value as any,
        settings as unknown as ISingleCellColumnSettings,
        meta?.resolvedColumnType,
        meta?.resolvedColumnSettings,
        meta?.timezone
      )
    }

    case ColumnTypes.Join:
    case ColumnTypes.Function: {
      return new FunctionCell(value, settings as unknown as IFunctionSettings, meta?.timezone)
    }
    case ColumnTypes.Math: {
      return new MathCell(value, settings as unknown as IMathColumnSettings)
    }
    case ColumnTypes.Template: {
      return new TemplateCell(value, settings as unknown as ITemplateColumnSettings)
    }

    /**
     * @todo:
     *  figure out how to handle default value for other columns,
     *  since converting other advanced columns into TextCell might be not ideal.
     */
    default: {
      return new TextCell(value, null)
    }
  }
}

/**
 * Extract the value of column in the settings based on available columns and rowData
 */
function extractSettingsValue(columnID: string, columnsMap: Map<string, ITableColumn>, rowData: ITableRow) {
  let convertedValue = ''

  let foundColumn = columnsMap.get(columnID)
  if (foundColumn) {
    let rawValue = rowData[foundColumn._id]
    convertedValue = parseStandardColumn(foundColumn, rawValue, columnsMap, rowData).toString()
  }

  return convertedValue
}

/**
 * Get qr code value and parsed its settings.
 * This method will use the column model intance suitable for QrCodeCell
 *
 * @todo clean up this logic after fixing the column def settings!
 */
function parseQrCodeColumn(
  settings: Record<string, any>,
  cellValue: RowValue,
  columnsMap: Map<string, ITableColumn>,
  rowData: ITableRow
): ICell {
  let qrCodeSettings = {
    qrType: settings.qrType,
  } as IQRCodeColumnSettings

  let qrCodeExtraSettings = {
    qrCrypto: settings.qrCrypto,
    qrEmail: settings.qrEmail,
    qrFile: settings.qrFile,
    qrSMS: settings.qrSMS,
    qrText: settings.qrText,
    qrURL: settings.qrURL,
    qrVCard: settings.qrVCard,
  } as IQRCodeColumnSettings

  qrCodeExtraSettings = Object.entries(qrCodeExtraSettings).reduce((acc, curr, i) => {
    let [key, settingsValue] = curr
    return {
      ...acc,
      [key]:
        typeof settingsValue === 'string'
          ? extractSettingsValue(settingsValue, columnsMap, rowData)
          : Object.entries(settingsValue).reduce(
              (valAcc, [settingsKey, columnID]) => {
                return {
                  ...valAcc,
                  [settingsKey]: extractSettingsValue(columnID as string, columnsMap, rowData),
                }
              },
              {} as Record<string, any>
            ),
    }
  }, {} as IQRCodeColumnSettings)

  qrCodeSettings = {
    ...qrCodeSettings,
    ...qrCodeExtraSettings,
  }

  return new QRCodeCell(null, qrCodeSettings)
}

/**
 * Get bar code value and parsed its settings.
 * This method will use the column model intance suitable for QrCodeCell
 *
 * @todo clean up this logic after fixing the column def settings!
 */
function parseBarCodeColumn(
  settings: Record<string, any>,
  cellValue: RowValue,
  columnsMap: Map<string, ITableColumn>,
  rowData: ITableRow
) {
  let barCodeSettings = {
    barcodeType: settings.barcodeType,
    barcodeColumn: extractSettingsValue(settings.barcodeColumn, columnsMap, rowData),
  } as IBarcodeSettings

  return new BarcodeCell(null, barCodeSettings)
}

/**
 * Get image value and parsed its settings.
 * This method will use the column model intance suitable for ImageCell
 *
 * @todo clean up this logic after fixing the column def settings!
 */
function parseImageColumn(
  settings: Record<string, any>,
  cellValue: RowValue,
  columnsMap: Map<string, ITableColumn>,
  rowData: ITableRow
) {
  let imageSettings = {
    imgSourceType: settings.imgSourceType,
    imgSourceYoutube: extractSettingsValue(settings.imgSourceYoutube, columnsMap, rowData),
  } as TImageSettings

  return new ImageCell(cellValue, imageSettings)
}

/**
 * Get slug value and parsed its settings.
 * This method will use the column model intance suitable for SlugCell
 *
 * @todo clean up this logic after fixing the column def settings!
 */
function parseSlugColumn(
  settings: Record<string, any>,
  cellValue: RowValue,
  columnsMap: Map<string, ITableColumn>,
  rowData: ITableRow,
  columnName?: string
) {
  const slugSettings = {
    slugColumn: extractSettingsValue(settings.slugColumn, columnsMap, rowData),
  }

  // generate value for slug
  // if slugColumn is not set, use slugify(columnName) and last six letters of rowId
  const slugValue = slugSettings.slugColumn ? slugSettings.slugColumn : (columnName ?? '')

  return new SlugCell(slugValue, {
    slugColumn: extractSettingsValue(settings.slugColumn, columnsMap, rowData),
    rowId: rowData._id,
  })
}
